<template>
  <div class="config-keys">
    <Row class="expand-header">
      <h3>
        {{ $t('configuration.confKey') }}
        <Button
          v-if="row.about.installed"
          class="add-confkey"
          icon="md-add"
          type="primary"
          ghost
          @click="showAddModal"
          shape="circle"
          size="small"
        ></Button>
      </h3>
    </Row>
    <Table :loading="loading" :columns="confKeyCol" :data="confKeys"></Table>

    <Modal
      v-model="modalVisible"
      class-name="confkey-modal--view view-form"
      :title="modalTitle"
      :width="650"
      :mask-closable="false"
      @on-visible-change="handleVisibleChange"
    >
      <template v-if="!isDetail">
        <Form ref="confKeyForm" :model="confKeyForm">
          <Row>
            <i-col span="24">
              <FormItem :label="$t('common.name')" prop="name" :required="!isDetail">
                <i-input v-model="confKeyForm.name" size="large" :disabled="isDetail || isEdit"></i-input>
              </FormItem>
            </i-col>
          </Row>
        </Form>
        <PropertyForm
          ref="propertyForm"
          from="sources"
          :modalVisible="modalVisible"
          :type="row.name"
          :properties="confKeysProperties.default"
          :form="confKeyForm"
          :sameLevelFormValid="sameLevelFormValid"
        ></PropertyForm>
      </template>
      <div v-else class="view-form">
        <div class="view-item" v-for="(val, label) in confKeyForm" :key="label">
          <label class="view-item--label">{{ label }}: </label>
          <template v-if="typeof val === 'object'">
            <KeyValueEditor ref="keyValueEditorRef" class="detail-key-value-table" :disabled="isDetail" :value="val" />
          </template>
          <span v-else>{{ val }}</span>
        </div>
      </div>
      <div slot="footer">
        <Button type="text" @click="modalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button v-if="!isDetail" type="primary" @click="save">{{ $t('common.submit') }}</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import nodeID from '@/mixins/nodeID'
import {
  getMetadataByCategory,
  getMetadataYamlByCategory,
  loadSourceDetail,
  addSourceConfKey,
  deleteSourceConfKey,
} from '@/api/metadata'
import { addConnectionsConfKey, deleteConnectionsConfKey, loadConnectionsDetail } from '@/api/configuration'
import PropertyForm from './PropertyForm.vue'
import KeyValueEditor from './KeyValueEditor.vue'
import { RESOURCE_TYPE } from '@/utils/constants'

export default {
  name: 'ConfigKeys',

  components: {
    PropertyForm,
    KeyValueEditor,
  },

  mixins: [nodeID],

  props: {
    row: {
      type: Object,
      required: true,
    },
    configType: {
      type: String,
    },
  },

  data() {
    return {
      RESOURCE_TYPE,
      category: RESOURCE_TYPE.sources,
      loading: false,
      modalVisible: false,
      confKeys: [],
      confKeyForm: {},
      isDetail: false,
      confKeysProperties: {
        default: [],
      },
      isEdit: false,
      confKeyCol: [
        {
          title: this.$t('common.name'),
          render: (h, params) => {
            const { name } = params.row
            return (
              <div>
                <a
                  href="javascript:;"
                  onClick={() => {
                    this.loadConfKeyDetail(name)
                  }}
                >
                  {name}
                </a>
              </div>
            )
          },
        },
        {
          title: this.$t('common.operations'),
          align: 'center',
          render: (h, params) => {
            const deleteOper = (
              <span>
                <a herf="javascript:;" style="margin-right:5px;" onClick={() => this.editConfKey(params.row)}>
                  <Icon type="ios-create-outline" />
                </a>
                <a herf="javascript:;" onClick={() => this.removeConfKey(params.row)}>
                  <Icon type="md-trash" />
                </a>
              </span>
            )
            const showDeleteIcon = this.row.about.installed && params.row.name !== 'default'
            return <div>{showDeleteIcon ? deleteOper : ''}</div>
          },
        },
      ],
      connectionsYamls: [],
      sameLevelFormValid: false,
    }
  },

  computed: {
    isConnection() {
      return this.configType === 'connection'
    },
    modalTitle() {
      let title = ''
      if (this.isEdit) {
        title = this.$t('configuration.editConfKey')
      } else {
        title = this.isDetail ? this.$t('configuration.viewConfKey') : this.$t('configuration.addConfKey')
      }
      return title
    },
  },

  created() {
    this.category = this.isConnection ? RESOURCE_TYPE.connections : RESOURCE_TYPE.sources

    if (this.isConnection) {
      this.loadDataDetail = loadConnectionsDetail
      this.addDataConfKey = addConnectionsConfKey
      this.deleteDataConfKey = deleteConnectionsConfKey
    } else {
      this.loadDataDetail = loadSourceDetail
      this.addDataConfKey = addSourceConfKey
      this.deleteDataConfKey = deleteSourceConfKey
    }
    this.loadData()
  },
  methods: {
    loadData() {
      this.loadConfKeyProperties()
    },
    // get conf key properties
    loadConfKeyProperties() {
      const data = {
        nodeID: this.nodeID,
        name: this.row.name,
      }
      getMetadataByCategory(this.category, data).then((res) => {
        getMetadataYamlByCategory(this.category, data).then((val) => {
          this.confKeys = Object.keys(val.data).map((item) => ({
            name: item,
          }))
          if (['mqtt', 'edgex'].includes(this.row.name)) {
            // connection
            getMetadataYamlByCategory('connections', data).then((value) => {
              this.confKeysProperties.default =
                res.data.properties.default.map((item) => {
                  item.default = this.getDefaultValue(val.data.default ? val.data.default[item.name] : '')
                  if (item.name === 'insecureSkipVerify') {
                    if (val.data.default && val.data.default[item.name] === undefined) {
                      item.default = true
                    }
                  }
                  if (item.name === 'connectionSelector') {
                    item.values = Object.keys(value.data)
                  }
                  return item
                }) || []
            })
          } else {
            // source config
            this.confKeysProperties.default =
              res.data.properties.default.map((item) => {
                if (item.control === 'list' && item.type === 'list_object') {
                  item.properties = item.default
                  item.default = this.getDefaultValue(val.data.default ? val.data.default[item.name] : '')
                } else {
                  item.default = this.getDefaultValue(val.data.default ? val.data.default[item.name] : '')
                }
                if (item.name === 'insecureSkipVerify') {
                  if (val.data.default && val.data.default[item.name] === undefined) {
                    item.default = true
                  }
                }
                return item
              }) || []
          }
        })
      })
    },

    getDefaultValue(value) {
      let result = value
      if (Array.isArray(value)) {
        result = value[0]
      }
      return result
    },

    // conf key detail
    loadConfKeyDetail(name) {
      this.isDetail = true
      this.modalVisible = true
      this.loadDataDetail(this.nodeID, this.row.name).then((res) => {
        const sourceData = res.data
        const confKeyData = sourceData[name]
        this.confKeyForm = {
          name,
          ...confKeyData,
        }
      })
    },

    // add conf key
    showAddModal() {
      this.isEdit = false
      this.isDetail = false
      this.modalVisible = true
    },
    // add | edit conf key dialog Visible
    handleVisibleChange(val) {
      if (!val) {
        this.isDetail = false
        this.confKeyForm = {}
      }
    },

    // remove empty key
    removeEmpetyObject(data) {
      Object.keys(data).forEach((key) => {
        const value = data[key]
        if (value) {
          if (typeof value === 'object') {
            let isEmpty = true
            Object.values(value).forEach((val) => {
              if (val !== undefined) {
                isEmpty = false
              }
            })
            if (isEmpty) {
              delete data[key]
            }
          }
        }
      })
    },

    async getSubmitData() {
      try {
        const confKeyFormRef = this.$refs.confKeyForm.$refs.confKeyForm || this.$refs.confKeyForm
        const propertyFormRef = this.$refs.confKeyForm.$refs.propertyForm || this.$refs.propertyForm

        const validConfKeyFormRes = await confKeyFormRef.validate()
        this.sameLevelFormValid = validConfKeyFormRes

        // propertyForm validate
        const { validatePropertiesForm } = propertyFormRef
        await validatePropertiesForm()

        if (!validConfKeyFormRes) {
          const errorMsg = this.$t('common.fillContent')
          this.$Message.error(errorMsg)
          return Promise.reject(new Error(errorMsg))
        }

        const propertyFormData = await propertyFormRef.getFormData()
        const { name, ...restPropertiesData } = propertyFormData
        let bodyData = restPropertiesData
        if (bodyData.connectionSelector) {
          const notRelated = []
          const notRelatedData = {}
          this.confKeysProperties.default.forEach((it) => {
            if (it.connection_related === false && bodyData[it.name]) {
              notRelated.push(it.name)
            }
          })
          notRelated.forEach((item) => {
            notRelatedData[item] = propertyFormData[item]
          })
          bodyData = {
            ...notRelatedData,
            connectionSelector: `${this.row.name}.${bodyData.connectionSelector}`,
          }
        }

        return Promise.resolve(bodyData)
      } catch (error) {
        console.error('error', error)
        return Promise.reject(error)
      }
    },

    async save() {
      const submitBodyData = await this.getSubmitData()
      this.removeEmpetyObject(submitBodyData)
      this.addDataConfKey(this.nodeID, this.row.name, this.confKeyForm.name, submitBodyData).then(() => {
        this.loadData()
        if (this.isEdit) {
          this.$Message.success(this.$t('common.editSuccess'))
        } else {
          this.$Message.success(this.$t('common.addSuccess'))
        }
        this.modalVisible = false
        this.confKeyForm = {}
      })
    },

    // edit conf key
    editConfKey({ name }) {
      this.loadDataDetail(this.nodeID, this.row.name).then((res) => {
        const sourceData = res.data
        const confKeyData = sourceData[name]
        const connectionForm = {}
        if (sourceData[name].connectionSelector) {
          const [connectionName, connectionType] = sourceData[name].connectionSelector.split('.')
          loadConnectionsDetail(this.nodeID, connectionName).then((result) => {
            const connectionData = result.data
            const connectionConfKeyData = connectionData[connectionType]
            Object.keys(connectionConfKeyData).forEach((item) => {
              connectionForm[item] = connectionConfKeyData[item]
            })
            this.confKeyForm = {
              name,
              ...confKeyData,
              connectionSelector: sourceData[name].connectionSelector.split('.')[1] || '',
              ...connectionForm,
            }
            this.isDetail = false
            this.modalVisible = true
            this.isEdit = true
          })
        } else {
          this.confKeyForm = {
            name,
            ...confKeyData,
          }
          this.isDetail = false
          this.modalVisible = true
          this.isEdit = true
        }
      })
    },

    // remove conf key
    removeConfKey({ name }) {
      this.$Modal.confirm({
        title: this.$t('common.warning'),
        content: `${this.$t('common.confirmDelete')} ${name}`,
        onOk: () => {
          this.deleteDataConfKey(this.nodeID, this.row.name, name).then(() => {
            this.loadData()
          })
        },
      })
    },
  },
}
</script>

<style lang="scss">
.config-keys {
  .expand-header {
    margin-bottom: 16px;
    h3 {
      font-size: 14px;
      .add-confkey {
        line-height: 24px;
        margin-left: 10px;
        margin-bottom: 5px;
        .ivu-icon {
          color: #34c388;
          font-size: 14px;
        }
      }
    }
  }
}
.confkey-modal--view {
  .detail-key-value-table {
    margin-top: 10px;
  }
}
</style>
